import { useMemo, useEffect } from 'react';
import { serializeError } from 'serialize-error';

import { LogLevel } from 'services/logger.service';
import { FileType, FileConfig } from 'services/file.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { getClipTime, getVideoUrl } from 'components/app/_helpers/_app.helper';
import { appLogger } from 'components/app/_mediator/app-logger.slice';
import { PlayerFrame } from 'components/app/app-components/frame-generator/frame-generator.helper';
import {
  ThumbnailData,
  ThumbnailStatus,
  uploadClipThumbnail,
  INIT_THUMBNAIL,
  updateClipThumbnails,
} from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { updThumbUrls } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { useCssRotate } from 'components/app/_mediator/css-rotate/use-css-rotate';
import { useCssFilter } from 'components/app/_mediator/css-filter/use-css-filter';
import { useCssFilterClip } from 'components/app/_mediator/css-filter/use-css-filter-clip';
import { useClipThumbContext } from 'components/app/_mediator/clip-thumb/clip-thumb.provider';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { FcClip } from 'components/app/finalcuts/_mediator/fc-data.slice';

import { FrameGeneratorVideo } from 'components/app/app-components/frame-generator/frame-generator-video';
import { FrameGeneratorImage } from 'components/app/app-components/frame-generator/frame-generator-image';
import styles from './_track-clip.module.scss';

const triedOnce = new Set<string>();

interface Props {
  clipData: Clip | FcClip;
  thumbnail: ThumbnailData;
  fileConfig?: FileConfig;
  finalCutId?: number;
}

export const ClipPoster = ({ clipData, fileConfig, thumbnail, finalCutId }: Props) => {
  const { files } = useAppSelector((state) => state.app__files);
  const { addThumbnailRef, removeThumbnailRef, getThumbnailSrc } = useClipThumbContext();
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);
  const clipThumbnails = useAppSelector((state) => state.clipThumb.clipThumbnails);
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const dispatch = useAppDispatch();

  const brightness = useCssFilterClip('brightness', clipData, fileConfig);
  const contrast = useCssFilterClip('contrast', clipData, fileConfig);
  const container = useCssFilter({ brightness, contrast });

  useCssRotate(container, { clipData, fileConfig });

  const file = useMemo(() => {
    return files[clipData.mediaFileId];
  }, [files]);

  const currentThumbnail: ThumbnailData = useMemo(() => {
    return clipThumbnails[clipData.clipId] ?? thumbnail;
  }, [clipThumbnails, thumbnail, clipData.clipId]);

  useEffect(() => {
    if (currentThumbnail.status === ThumbnailStatus.readyToUse) {
      removeThumbnailRef(clipData.clipId);
    }
  }, [currentThumbnail.status]);

  useEffect(() => {
    return () => {
      triedOnce.delete(clipData.clipId);
    };
  }, [clipData.clipId]);

  const handleFrame = (frame: PlayerFrame) => {
    if (!selectedProject || !selectedStory) return;
    addThumbnailRef(clipData.clipId, frame.dataUri);
    dispatch(
      uploadClipThumbnail({
        finalCutId,
        storyId: selectedStory,
        projectId: selectedProject,
        clipData,
        blob: frame.blob,
      })
    );
  };

  const onError = async (e: React.SyntheticEvent<HTMLImageElement>) => {
    const src = e.currentTarget.src;

    let httpStatus: number | null = null;
    let httpStatusText: string | null = null;
    let fetchError: unknown = null;

    try {
      const res = await fetch(src, { method: 'HEAD' });
      httpStatus = res.status;
      httpStatusText = res.statusText;
    } catch (err) {
      fetchError = serializeError(err);
    }

    dispatch(
      appLogger({
        level: LogLevel.Error,
        error: {
          name: 'Thumbnail load error log',
          message: JSON.stringify({
            src,
            httpStatus,
            httpStatusText,
            fetchError,
            online: navigator.onLine,
            triedOnce: triedOnce.has(clipData.clipId),
            thumbnailStatus: thumbnail.status,
          }),
        },
      })
    );

    // prevent duplicate regeneration while one is in progress or already attempted
    if (triedOnce.has(clipData.clipId)) return;
    if (thumbnail.status === ThumbnailStatus.init || thumbnail.status === ThumbnailStatus.pending) return;

    // 403: likely expired/forbidden signed URL -> refresh URLs; no regeneration
    if (httpStatus === 403) {
      if (selectedStory) dispatch(updThumbUrls(selectedStory));
      return;
    }

    // 404: truly missing -> regenerate once per session
    if (!navigator.onLine || httpStatus !== 404) return;

    triedOnce.add(clipData.clipId);
    dispatch(updateClipThumbnails({ [clipData.clipId]: INIT_THUMBNAIL }));
  };

  return (
    <div className={styles.ThumbnailGenerator}>
      <div ref={container}>
        {currentThumbnail.status === ThumbnailStatus.init ? (
          file.fileType === FileType.image ? (
            <FrameGeneratorImage url={getVideoUrl(file)} cb={handleFrame} />
          ) : (
            <FrameGeneratorVideo url={getVideoUrl(file)} cb={handleFrame} position={getClipTime(clipData) ?? 0} />
          )
        ) : (
          <img
            src={getThumbnailSrc(clipData.clipId, clipThumbnails, currentThumbnail)}
            crossOrigin="anonymous"
            onError={onError}
          />
        )}
      </div>
    </div>
  );
};
